import { useState, useMemo, useRef, type ChangeEvent, type DragEvent } from 'react';
import {
  X,
  Layers,
  FileText,
  Search,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  UploadCloud,
  Trash2,
  BookOpen,
  CheckSquare,
  Square,
  Sparkles,
} from 'lucide-react';
import { Button, LoadingSpinner } from '@lms/ui';
import {
  useEvaluations,
  useEvaluationDetail,
  useImportEvaluationQuestions,
  useBulkAddEvaluationQuestions,
} from '../hooks/useEvaluations';
import { useLanguage } from '../lib/LanguageContext';
import { Bilingual } from './Bilingual';
import { copy } from '../copy/copy';
import {
  parseQuestionsFromText,
  validateParsedQuestion,
  type ParsedQuestion,
} from '../utils/questionParser';

interface ImportQuestionsModalProps {
  currentEvaluationId: string;
  onClose: () => void;
}

export function ImportQuestionsModal({ currentEvaluationId, onClose }: ImportQuestionsModalProps) {
  const { language, t } = useLanguage();
  const [importMode, setImportMode] = useState<'EXISTING' | 'TEXT'>('EXISTING');

  // ==========================================
  // TAB 1: FROM OTHER EVALUATION (BANK SOAL)
  // ==========================================
  const { data: allEvaluations, isLoading: isLoadingAllEvals } = useEvaluations();
  const [searchSourceQuery, setSearchSourceQuery] = useState('');
  const [filterType, setFilterType] = useState<string>('ALL');
  const [filterHasQuestions, setFilterHasQuestions] = useState(true);
  const [selectedSourceId, setSelectedSourceId] = useState<string>('');

  const { data: sourceEvaluationDetail, isLoading: isLoadingSourceDetail } =
    useEvaluationDetail(selectedSourceId);

  // Selected question IDs from source evaluation (empty array = none selected; initially all are selected upon choosing source)
  const [selectedSourceQuestionIds, setSelectedSourceQuestionIds] = useState<string[]>([]);

  const handleSelectSourceEval = (evalId: string) => {
    setSelectedSourceId(evalId);
    const selectedEvaluation = allEvaluations?.find(evaluation => evaluation.id === evalId);
    setSelectedSourceQuestionIds(selectedEvaluation?.questions?.map(question => question.id) || []);
  };

  // Filtered source evaluations
  const filteredEvaluations = useMemo(() => {
    return (allEvaluations || []).filter(ev => {
      // Exclude current evaluation
      if (ev.id === currentEvaluationId) return false;

      // Filter has questions (>0)
      const qCount = ev.questions?.length || 0;
      if (filterHasQuestions && qCount === 0) return false;

      // Filter by type
      if (filterType !== 'ALL' && ev.type !== filterType) return false;

      // Filter by search query
      if (searchSourceQuery.trim()) {
        const query = searchSourceQuery.toLowerCase();
        const titleMatch = ev.title?.toLowerCase().includes(query);
        const descMatch = ev.description?.toLowerCase().includes(query);
        const typeMatch = ev.type?.toLowerCase().includes(query);
        if (!titleMatch && !descMatch && !typeMatch) return false;
      }

      return true;
    });
  }, [allEvaluations, currentEvaluationId, filterHasQuestions, filterType, searchSourceQuery]);

  // Set all questions selected by default when source detail loads
  const currentSourceQuestions = sourceEvaluationDetail?.questions || [];
  const allSourceQuestionsSelected =
    currentSourceQuestions.length > 0 &&
    selectedSourceQuestionIds.length === currentSourceQuestions.length;

  const toggleSelectAllSourceQuestions = () => {
    if (allSourceQuestionsSelected) {
      setSelectedSourceQuestionIds([]);
    } else {
      setSelectedSourceQuestionIds(currentSourceQuestions.map(q => q.id));
    }
  };

  const toggleSourceQuestion = (qId: string) => {
    setSelectedSourceQuestionIds(prev =>
      prev.includes(qId) ? prev.filter(id => id !== qId) : [...prev, qId]
    );
  };

  // ==========================================
  // TAB 2: FROM DOCUMENT / TEXT
  // ==========================================
  const [docInputMethod, setDocInputMethod] = useState<'PASTE' | 'UPLOAD'>('PASTE');
  const [importText, setImportText] = useState('');
  const [defaultPoints, setDefaultPoints] = useState(10);
  const [parsedQuestions, setParsedQuestions] = useState<ParsedQuestion[]>([]);
  const [selectedParsedIds, setSelectedParsedIds] = useState<string[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleParseText = () => {
    if (!importText.trim()) return;
    const parsed = parseQuestionsFromText(importText, defaultPoints);
    setParsedQuestions(parsed);
    setSelectedParsedIds(parsed.map(q => q.id || ''));
  };

  const handleFileUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = e => {
      const content = e.target?.result as string;
      if (content) {
        setImportText(content);
        const parsed = parseQuestionsFromText(content, defaultPoints);
        setParsedQuestions(parsed);
        setSelectedParsedIds(parsed.map(q => q.id || ''));
      }
    };
    reader.readAsText(file);
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDownloadTemplate = (format: 'txt' | 'csv' | 'json') => {
    let content = '';
    let filename = '';
    let mimeType = 'text/plain';

    if (format === 'txt') {
      content = `1. Siapakah Nabi dan Rasul terakhir yang diutus oleh Allah Ta'ala?
A. Nabi Musa 'alaihissalam
B. Nabi Isa 'alaihissalam
C. Nabi Muhammad shallallahu 'alaihi wa sallam
D. Nabi Ibrahim 'alaihissalam
Kunci: C
Pembahasan: Nabi Muhammad shallallahu 'alaihi wa sallam adalah penutup para Nabi dan Rasul (Khatamun Nabiyyin).

2. Rukun Islam terdiri dari 5 perkara.
A. Benar
B. Salah
Kunci: A
Pembahasan: Rukun Islam ada 5 yaitu Syahadat, Shalat, Zakat, Puasa, dan Haji.
`;
      filename = 'Template_Soal_LMS.txt';
    } else if (format === 'csv') {
      content = `Pertanyaan,Pilihan A,Pilihan B,Pilihan C,Pilihan D,Kunci,Bobot
"Rukun Islam yang pertama adalah?","Shalat","Syahadat","Puasa","Zakat","B",10
"Berapakah jumlah rakaat shalat Subuh?","2 Rakaat","3 Rakaat","4 Rakaat","1 Rakaat","A",10
`;
      filename = 'Template_Soal_LMS.csv';
      mimeType = 'text/csv';
    } else if (format === 'json') {
      content = JSON.stringify(
        [
          {
            question: 'Berapakah jumlah rukun iman?',
            type: 'MULTIPLE_CHOICE',
            points: 10,
            explanation: 'Rukun iman ada 6 perkara.',
            options: [
              { text: '5 Perkara', isCorrect: false },
              { text: '6 Perkara', isCorrect: true },
              { text: '7 Perkara', isCorrect: false },
              { text: '4 Perkara', isCorrect: false },
            ],
          },
        ],
        null,
        2
      );
      filename = 'Template_Soal_LMS.json';
      mimeType = 'application/json';
    }

    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // In-place edits for parsed questions
  const updateParsedQuestionOption = (qIdx: number, optIdx: number, isCorrect: boolean) => {
    setParsedQuestions(prev => {
      const next = [...prev];
      const targetQ = { ...next[qIdx] };
      const nextOptions = targetQ.options.map((opt, i) => ({
        ...opt,
        isCorrect: i === optIdx ? isCorrect : false,
      }));
      targetQ.options = nextOptions;
      targetQ.warnings = validateParsedQuestion(targetQ);
      next[qIdx] = targetQ;
      return next;
    });
  };

  const updateParsedQuestionPoints = (qIdx: number, points: number) => {
    setParsedQuestions(prev => {
      const next = [...prev];
      next[qIdx] = { ...next[qIdx], points };
      return next;
    });
  };

  const deleteParsedQuestion = (qIdx: number) => {
    setParsedQuestions(prev => prev.filter((_, i) => i !== qIdx));
  };

  const toggleSelectParsedQuestion = (id: string) => {
    setSelectedParsedIds(prev => (prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]));
  };

  const allParsedSelected =
    parsedQuestions.length > 0 && selectedParsedIds.length === parsedQuestions.length;

  const toggleSelectAllParsed = () => {
    if (allParsedSelected) {
      setSelectedParsedIds([]);
    } else {
      setSelectedParsedIds(parsedQuestions.map(q => q.id || ''));
    }
  };

  // ==========================================
  // MUTATIONS & SUBMIT
  // ==========================================
  const importQuestionsMutation = useImportEvaluationQuestions();
  const bulkAddMutation = useBulkAddEvaluationQuestions();
  const [importError, setImportError] = useState<string | null>(null);

  const runImportFromExisting = async () => {
    if (!selectedSourceId || selectedSourceQuestionIds.length === 0) return;
    setImportError(null);
    try {
      await importQuestionsMutation.mutateAsync({
        evaluationId: currentEvaluationId,
        sourceEvaluationId: selectedSourceId,
        questionIds: selectedSourceQuestionIds,
      });
      onClose();
    } catch (err) {
      setImportError(
        (err as Error)?.message ?? copy.evaluationDetail.importErrorExisting[language]
      );
    }
  };

  const runBulkAddFromText = async () => {
    const questionsToSubmit = parsedQuestions.filter(q => selectedParsedIds.includes(q.id || ''));
    if (questionsToSubmit.length === 0) return;
    setImportError(null);
    try {
      await bulkAddMutation.mutateAsync({
        evaluationId: currentEvaluationId,
        questions: questionsToSubmit,
      });
      onClose();
    } catch (err) {
      setImportError((err as Error)?.message ?? copy.evaluationDetail.importErrorBulk[language]);
    }
  };

  // Preview stats for parsed questions
  const totalParsedPoints = useMemo(() => {
    return parsedQuestions
      .filter(q => selectedParsedIds.includes(q.id || ''))
      .reduce((acc, q) => acc + (q.points || 0), 0);
  }, [parsedQuestions, selectedParsedIds]);

  const hasWarnings = useMemo(() => {
    return parsedQuestions.some(
      q => selectedParsedIds.includes(q.id || '') && (q.warnings?.length || 0) > 0
    );
  }, [parsedQuestions, selectedParsedIds]);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 md:p-6 bg-surface/80 backdrop-blur-sm animate-fadeIn">
      <div className="bg-surface-container-lowest w-full sm:max-w-4xl rounded-t-3xl sm:rounded-3xl shadow-level4 border border-on-surface/5 flex flex-col h-[94vh] sm:max-h-[88vh] overflow-hidden animate-scaleIn">
        {/* Modal Header */}
        <div className="p-5 sm:p-6 border-b border-on-surface/5 bg-surface-container-low/40 flex items-center justify-between shrink-0">
          <div>
            <div className="flex items-center gap-2">
              <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-primary/10 text-primary">
                Import Wizard
              </span>
            </div>
            <h2 className="text-xl sm:text-2xl font-headline font-black text-on-surface tracking-tight mt-1 m-0">
              <Bilingual {...copy.evaluationDetail.btnImportQuestionNodes} />
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-xl text-on-surface-variant hover:bg-surface-container hover:text-on-surface transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Tab Selection Segments */}
        <div className="px-5 sm:px-6 pt-3 pb-2 border-b border-on-surface/5 bg-surface-container-low/20 shrink-0">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                setImportMode('EXISTING');
                setImportError(null);
              }}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-label font-bold uppercase tracking-wider transition-all ${
                importMode === 'EXISTING'
                  ? 'bg-primary text-on-primary shadow-level1'
                  : 'bg-surface-container hover:bg-surface-container-high text-on-surface-variant'
              }`}
            >
              <Layers size={15} />
              <span>
                <Bilingual {...copy.evaluationDetail.tabBankSoal} />
              </span>
            </button>
            <button
              type="button"
              onClick={() => {
                setImportMode('TEXT');
                setImportError(null);
              }}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-label font-bold uppercase tracking-wider transition-all ${
                importMode === 'TEXT'
                  ? 'bg-primary text-on-primary shadow-level1'
                  : 'bg-surface-container hover:bg-surface-container-high text-on-surface-variant'
              }`}
            >
              <FileText size={15} />
              <span>
                <Bilingual {...copy.evaluationDetail.tabDocument} />
              </span>
            </button>
          </div>
        </div>

        {/* Modal Body (Scrollable) */}
        <div className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-6">
          {/* ============================================================ */}
          {/* TAB 1: BANK SOAL EVALUASI LAIN */}
          {/* ============================================================ */}
          {importMode === 'EXISTING' && (
            <div className="space-y-6">
              {/* Filter & Search Bar */}
              <div className="space-y-3 p-4 rounded-2xl bg-surface-container-low/50 border border-on-surface/5">
                <div className="flex flex-col sm:flex-row gap-3">
                  {/* Search Bar */}
                  <div className="relative flex-1">
                    <Search
                      size={16}
                      className="absolute left-3.5 top-1/2 -translate-y-1/2 text-on-surface-variant opacity-60"
                    />
                    <input
                      type="text"
                      placeholder={t(copy.evaluationDetail.searchSourceEvalPlaceholder)}
                      value={searchSourceQuery}
                      onChange={e => setSearchSourceQuery(e.target.value)}
                      className="w-full bg-surface-container border-none rounded-xl pl-10 pr-4 py-2.5 text-xs font-body text-on-surface outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                    />
                    {searchSourceQuery && (
                      <button
                        type="button"
                        onClick={() => setSearchSourceQuery('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-on-surface-variant hover:text-on-surface"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>

                  {/* Type Filter */}
                  <select
                    value={filterType}
                    onChange={e => setFilterType(e.target.value)}
                    aria-label="Filter Tipe Evaluasi"
                    className="bg-surface-container border-none text-on-surface font-body font-bold text-xs rounded-xl px-4 py-2.5 outline-none focus:ring-2 focus:ring-primary/20 transition-all min-w-[140px]"
                  >
                    <option value="ALL">{t(copy.evaluationDetail.filterAllTypes)}</option>
                    <option value="EXAM">EXAM (Ujian)</option>
                    <option value="QUIZ">QUIZ (Kuis)</option>
                    <option value="ASSIGNMENT">ASSIGNMENT (Tugas)</option>
                    <option value="PRACTICE">PRACTICE (Latihan)</option>
                  </select>
                </div>

                {/* Has Questions Toggle Filter */}
                <div className="flex items-center justify-between pt-1 text-xs font-body">
                  <label className="flex items-center gap-2 cursor-pointer select-none text-on-surface-variant">
                    <input
                      type="checkbox"
                      checked={filterHasQuestions}
                      onChange={e => setFilterHasQuestions(e.target.checked)}
                      className="w-4 h-4 rounded text-primary accent-primary bg-surface-container border-none focus:ring-0 cursor-pointer"
                    />
                    <span className="font-medium">
                      <Bilingual {...copy.evaluationDetail.filterHasQuestionsOnly} />
                    </span>
                  </label>
                  <span className="text-[11px] text-on-surface-variant opacity-70 font-mono">
                    {filteredEvaluations.length} evaluasi ditemukan
                  </span>
                </div>
              </div>

              {/* Evaluations Selection Grid */}
              <div className="space-y-2">
                <label className="text-[10px] font-label font-black uppercase tracking-widest text-on-surface-variant ml-1 block">
                  <Bilingual {...copy.evaluationDetail.labelSelectSourceEval} />
                </label>

                {isLoadingAllEvals ? (
                  <div className="py-12 flex justify-center items-center">
                    <LoadingSpinner size="md" />
                  </div>
                ) : filteredEvaluations.length === 0 ? (
                  <div className="text-center py-10 px-4 bg-surface-container-low/30 rounded-2xl border border-dashed border-on-surface/10">
                    <BookOpen
                      size={36}
                      className="mx-auto mb-2 opacity-30 text-on-surface-variant"
                    />
                    <p className="text-xs font-body text-on-surface-variant italic">
                      <Bilingual {...copy.evaluationDetail.emptyEvaluationsFound} />
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-h-56 overflow-y-auto p-1">
                    {filteredEvaluations.map(ev => {
                      const isSelected = selectedSourceId === ev.id;
                      const qCount = ev.questions?.length || 0;
                      return (
                        <div
                          key={ev.id}
                          onClick={() => handleSelectSourceEval(ev.id)}
                          className={`p-3.5 rounded-2xl cursor-pointer transition-all border text-left flex flex-col justify-between gap-2 ${
                            isSelected
                              ? 'bg-primary/10 border-primary/40 shadow-sm ring-1 ring-primary/30'
                              : 'bg-surface-container-low hover:bg-surface-container border-on-surface/5'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <span className="text-xs font-headline font-bold text-on-surface line-clamp-2">
                              {ev.title}
                            </span>
                            <span
                              className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase shrink-0 ${
                                ev.type === 'EXAM'
                                  ? 'bg-primary/15 text-primary'
                                  : ev.type === 'QUIZ'
                                    ? 'bg-secondary/20 text-secondary'
                                    : 'bg-surface-container-high text-on-surface-variant'
                              }`}
                            >
                              {ev.type}
                            </span>
                          </div>

                          <div className="flex items-center justify-between text-[10px] font-body text-on-surface-variant pt-1 border-t border-on-surface/5">
                            <span className="font-mono font-bold text-primary">
                              {qCount} {t(copy.evaluationDetail.optionCountSuffix)}
                            </span>
                            {ev.passingScore != null && <span>Passing: {ev.passingScore}%</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Source Questions Preview & Cherry-Picking Section */}
              {selectedSourceId && (
                <div className="space-y-3 pt-2">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <h3 className="text-sm font-headline font-bold text-on-surface flex items-center gap-2 m-0">
                        <Bilingual {...copy.evaluationDetail.labelPreviewQuestions} />
                        <span className="text-xs font-mono px-2 py-0.5 rounded-full bg-surface-container font-bold text-primary">
                          {selectedSourceQuestionIds.length} / {currentSourceQuestions.length}{' '}
                          {t(copy.evaluationDetail.selectedQuestionsCount)}
                        </span>
                      </h3>
                    </div>

                    {currentSourceQuestions.length > 0 && (
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={toggleSelectAllSourceQuestions}
                          className="text-[11px] font-label font-bold text-primary uppercase tracking-wider hover:underline flex items-center gap-1.5"
                        >
                          {allSourceQuestionsSelected ? (
                            <>
                              <Square size={13} />
                              <Bilingual {...copy.evaluationDetail.btnDeselectAll} />
                            </>
                          ) : (
                            <>
                              <CheckSquare size={13} />
                              <Bilingual {...copy.evaluationDetail.btnSelectAll} />
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </div>

                  {isLoadingSourceDetail ? (
                    <div className="py-12 flex justify-center items-center bg-surface-container/20 rounded-2xl">
                      <LoadingSpinner size="md" />
                    </div>
                  ) : currentSourceQuestions.length === 0 ? (
                    <div className="text-center py-10 bg-surface-container-low/30 rounded-2xl border border-dashed border-on-surface/10">
                      <p className="text-xs font-body text-on-surface-variant italic">
                        <Bilingual {...copy.evaluationDetail.emptySelectedQuestions} />
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-72 overflow-y-auto p-1">
                      {currentSourceQuestions.map((q, idx) => {
                        const isChecked = selectedSourceQuestionIds.includes(q.id);
                        return (
                          <div
                            key={q.id}
                            onClick={() => toggleSourceQuestion(q.id)}
                            className={`p-4 rounded-2xl cursor-pointer transition-all border text-left space-y-2.5 ${
                              isChecked
                                ? 'bg-surface-container-lowest border-primary/30 shadow-xs'
                                : 'bg-surface-container-low/40 border-on-surface/5 opacity-70'
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => toggleSourceQuestion(q.id)}
                                onClick={e => e.stopPropagation()}
                                className="mt-0.5 w-4 h-4 rounded text-primary accent-primary bg-surface-container border-none focus:ring-0 cursor-pointer"
                              />
                              <div className="flex-1 space-y-1">
                                <div className="flex items-center justify-between gap-2">
                                  <span className="text-[10px] font-mono font-bold text-on-surface-variant">
                                    Soal #{idx + 1}
                                  </span>
                                  <span className="px-2 py-0.5 rounded text-[9px] font-mono font-bold bg-primary/10 text-primary uppercase">
                                    {q.type} • {q.points} PTS
                                  </span>
                                </div>
                                <p className="text-xs sm:text-sm font-body font-semibold text-on-surface leading-snug">
                                  {q.question}
                                </p>
                              </div>
                            </div>

                            {/* Options List Preview */}
                            {q.options && q.options.length > 0 && (
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 pl-7 pt-1">
                                {q.options.map((opt, optIdx) => (
                                  <div
                                    key={opt.id || optIdx}
                                    className={`px-3 py-1.5 rounded-xl text-xs font-body flex items-center justify-between gap-2 ${
                                      opt.isCorrect
                                        ? 'bg-emerald-500/10 text-emerald-700 font-bold border border-emerald-500/20'
                                        : 'bg-surface-container/60 text-on-surface-variant'
                                    }`}
                                  >
                                    <span className="truncate">
                                      {String.fromCharCode(65 + optIdx)}. {opt.text}
                                    </span>
                                    {opt.isCorrect && (
                                      <CheckCircle2
                                        size={13}
                                        className="text-emerald-600 shrink-0"
                                      />
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}

                            {q.explanation && (
                              <div className="pl-7 pt-1 text-[11px] font-body text-on-surface-variant italic">
                                <span className="font-bold text-primary">Pembahasan: </span>
                                {q.explanation}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ============================================================ */}
          {/* TAB 2: DARI DOKUMEN / FILE / TEKS */}
          {/* ============================================================ */}
          {importMode === 'TEXT' && (
            <div className="space-y-6">
              {parsedQuestions.length === 0 ? (
                /* Step 1: Input / Upload */
                <div className="space-y-4">
                  {/* Top Bar: Input Mode Tabs + Download Templates */}
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setDocInputMethod('PASTE')}
                        className={`px-3.5 py-1.5 rounded-xl text-xs font-label font-bold uppercase tracking-wider transition-all ${
                          docInputMethod === 'PASTE'
                            ? 'bg-surface-container-high text-primary'
                            : 'text-on-surface-variant hover:text-on-surface'
                        }`}
                      >
                        <Bilingual {...copy.evaluationDetail.directPasteTab} />
                      </button>
                      <button
                        type="button"
                        onClick={() => setDocInputMethod('UPLOAD')}
                        className={`px-3.5 py-1.5 rounded-xl text-xs font-label font-bold uppercase tracking-wider transition-all ${
                          docInputMethod === 'UPLOAD'
                            ? 'bg-surface-container-high text-primary'
                            : 'text-on-surface-variant hover:text-on-surface'
                        }`}
                      >
                        <Bilingual {...copy.evaluationDetail.fileUploadTab} />
                      </button>
                    </div>

                    {/* Download Sample Templates */}
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-label text-on-surface-variant uppercase font-bold">
                        Template:
                      </span>
                      <button
                        type="button"
                        onClick={() => handleDownloadTemplate('txt')}
                        className="px-2.5 py-1 rounded-lg bg-surface-container text-[10px] font-mono font-bold text-on-surface-variant hover:text-primary transition-colors"
                        title="Download Format Teks (.txt)"
                      >
                        .TXT
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDownloadTemplate('csv')}
                        className="px-2.5 py-1 rounded-lg bg-surface-container text-[10px] font-mono font-bold text-on-surface-variant hover:text-primary transition-colors"
                        title="Download Format CSV (.csv)"
                      >
                        .CSV
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDownloadTemplate('json')}
                        className="px-2.5 py-1 rounded-lg bg-surface-container text-[10px] font-mono font-bold text-on-surface-variant hover:text-primary transition-colors"
                        title="Download Format JSON (.json)"
                      >
                        .JSON
                      </button>
                    </div>
                  </div>

                  {/* Drag & Drop File Upload Area */}
                  {docInputMethod === 'UPLOAD' && (
                    <div
                      onDrop={handleDrop}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onClick={() => fileInputRef.current?.click()}
                      className={`p-8 rounded-3xl border-2 border-dashed text-center cursor-pointer transition-all flex flex-col items-center justify-center gap-3 ${
                        isDragging
                          ? 'border-primary bg-primary/10'
                          : 'border-outline-variant/30 bg-surface-container-low/40 hover:bg-surface-container-low'
                      }`}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".txt,.md,.csv,.json"
                        onChange={handleFileChange}
                        className="hidden"
                      />
                      <div className="p-4 rounded-2xl bg-primary/10 text-primary">
                        <UploadCloud size={32} />
                      </div>
                      <div>
                        <p className="text-xs sm:text-sm font-headline font-bold text-on-surface m-0">
                          <Bilingual {...copy.evaluationDetail.dragDropTitle} />
                        </p>
                        <p className="text-[11px] font-body text-on-surface-variant mt-1 opacity-70">
                          <Bilingual {...copy.evaluationDetail.dragDropFormats} />
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Direct Text Editor */}
                  {docInputMethod === 'PASTE' && (
                    <div className="space-y-2">
                      <textarea
                        className="w-full bg-surface-container border-none rounded-2xl p-4 sm:p-5 font-mono text-xs text-on-surface outline-none resize-none min-h-[220px] focus:ring-2 focus:ring-primary/20 transition-all leading-relaxed"
                        placeholder={`1. Pertanyaan contoh pertama Anda?\nA. Pilihan pertama\nB. Pilihan kedua\nC. Pilihan ketiga\nD. Pilihan keempat\nKunci: A\nPembahasan: Penjelasan mengapa opsi A benar.\n\n2. Pertanyaan kedua?\nA. Pilihan A\nB. Pilihan B\nKunci: B`}
                        value={importText}
                        onChange={e => setImportText(e.target.value)}
                      />
                    </div>
                  )}

                  {/* Config Default Points & Parse Action */}
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-2">
                    <div className="flex items-center gap-3">
                      <label className="text-xs font-body font-medium text-on-surface-variant">
                        <Bilingual {...copy.evaluationDetail.defaultPointsLabel} />:
                      </label>
                      <input
                        type="number"
                        min={1}
                        max={100}
                        value={defaultPoints}
                        onChange={e => setDefaultPoints(parseInt(e.target.value, 10) || 10)}
                        className="w-20 bg-surface-container border-none rounded-xl px-3 py-2 text-xs font-mono font-bold text-center text-on-surface outline-none"
                      />
                    </div>

                    <Button
                      onClick={handleParseText}
                      disabled={!importText.trim()}
                      className="h-11 px-6 rounded-xl text-xs font-label font-bold uppercase tracking-wider shadow-level2"
                    >
                      <Sparkles size={15} className="mr-1.5" />
                      <Bilingual {...copy.evaluationDetail.btnParseAndPreview} />
                    </Button>
                  </div>
                </div>
              ) : (
                /* Step 2: Interactive Pratinjau (Preview) & In-Place Edit */
                <div className="space-y-4">
                  {/* KPI Summary Banner */}
                  <div className="p-4 rounded-2xl bg-surface-container-low border border-on-surface/5 flex flex-wrap items-center justify-between gap-4">
                    <div className="flex flex-wrap items-center gap-4">
                      <div>
                        <span className="text-[10px] font-label font-bold uppercase tracking-wider text-on-surface-variant opacity-70 block">
                          <Bilingual {...copy.evaluationDetail.totalParsed} />
                        </span>
                        <p className="text-xl font-headline font-black text-primary m-0">
                          {selectedParsedIds.length} / {parsedQuestions.length}{' '}
                          <span className="text-xs font-normal opacity-70">butir</span>
                        </p>
                      </div>

                      <div className="h-8 w-px bg-on-surface/10 hidden sm:block" />

                      <div>
                        <span className="text-[10px] font-label font-bold uppercase tracking-wider text-on-surface-variant opacity-70 block">
                          <Bilingual {...copy.evaluationDetail.totalPoints} />
                        </span>
                        <p className="text-xl font-headline font-black text-secondary m-0">
                          {totalParsedPoints}{' '}
                          <span className="text-xs font-normal opacity-70">PTS</span>
                        </p>
                      </div>

                      <div className="h-8 w-px bg-on-surface/10 hidden sm:block" />

                      <div>
                        <span className="text-[10px] font-label font-bold uppercase tracking-wider text-on-surface-variant opacity-70 block">
                          Status Validasi
                        </span>
                        {hasWarnings ? (
                          <span className="inline-flex items-center gap-1 text-xs font-bold text-amber-600">
                            <AlertTriangle size={14} />
                            <span>Perlu Perhatian</span>
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-600">
                            <CheckCircle2 size={14} />
                            <span>Siap Diimpor</span>
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={toggleSelectAllParsed}
                        className="text-[11px] font-label font-bold text-primary uppercase tracking-wider hover:underline"
                      >
                        {allParsedSelected
                          ? t(copy.evaluationDetail.btnDeselectAll)
                          : t(copy.evaluationDetail.btnSelectAll)}
                      </button>
                      <button
                        type="button"
                        onClick={() => setParsedQuestions([])}
                        className="px-3 py-1.5 rounded-xl bg-surface-container text-[11px] font-label font-bold text-on-surface-variant hover:text-on-surface uppercase tracking-wider transition-colors"
                      >
                        <Bilingual {...copy.evaluationDetail.btnBackToEditor} />
                      </button>
                    </div>
                  </div>

                  {/* Parsed Questions Cards List */}
                  <div className="space-y-4 max-h-[50vh] overflow-y-auto p-1">
                    {parsedQuestions.map((q, qIdx) => {
                      const isChecked = selectedParsedIds.includes(q.id || '');
                      const warnings = q.warnings || [];
                      return (
                        <div
                          key={q.id || qIdx}
                          className={`p-4 sm:p-5 rounded-2xl border transition-all space-y-3 ${
                            isChecked
                              ? warnings.length > 0
                                ? 'bg-surface-container-lowest border-amber-500/30'
                                : 'bg-surface-container-lowest border-primary/20 shadow-xs'
                              : 'bg-surface-container-low/40 border-on-surface/5 opacity-60'
                          }`}
                        >
                          {/* Question Card Header */}
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-3 flex-1">
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => toggleSelectParsedQuestion(q.id || '')}
                                className="mt-1 w-4 h-4 rounded text-primary accent-primary bg-surface-container border-none focus:ring-0 cursor-pointer"
                              />
                              <div className="flex-1 space-y-1">
                                <div className="flex items-center gap-2">
                                  <span className="text-[10px] font-mono font-bold text-on-surface-variant">
                                    Soal #{qIdx + 1}
                                  </span>
                                  <span className="px-2 py-0.5 rounded text-[9px] font-mono font-bold bg-primary/10 text-primary uppercase">
                                    {q.type}
                                  </span>
                                </div>
                                <p className="text-xs sm:text-sm font-body font-semibold text-on-surface leading-snug">
                                  {q.question}
                                </p>
                              </div>
                            </div>

                            {/* Points & Delete action */}
                            <div className="flex items-center gap-2">
                              <div className="flex items-center gap-1 bg-surface-container px-2 py-1 rounded-lg">
                                <input
                                  type="number"
                                  min={1}
                                  max={100}
                                  value={q.points}
                                  onChange={e =>
                                    updateParsedQuestionPoints(
                                      qIdx,
                                      parseInt(e.target.value, 10) || 10
                                    )
                                  }
                                  className="w-10 bg-transparent border-none p-0 text-[11px] font-mono font-bold text-center text-on-surface outline-none"
                                />
                                <span className="text-[9px] font-bold text-on-surface-variant">
                                  PTS
                                </span>
                              </div>
                              <button
                                type="button"
                                onClick={() => deleteParsedQuestion(qIdx)}
                                className="p-1.5 text-on-surface-variant hover:text-error transition-colors rounded-lg"
                                title="Hapus soal ini dari daftar impor"
                              >
                                <Trash2 size={15} />
                              </button>
                            </div>
                          </div>

                          {/* Warning Banner if question has errors */}
                          {warnings.length > 0 && (
                            <div className="p-2.5 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center gap-2 text-xs text-amber-800">
                              <AlertTriangle size={14} className="shrink-0 text-amber-600" />
                              <span className="text-[11px] font-body">{warnings.join(', ')}</span>
                            </div>
                          )}

                          {/* Options Radio List (Interactive Selection) */}
                          <div className="space-y-1.5 pl-7">
                            <span className="text-[10px] font-label font-bold uppercase tracking-wider text-on-surface-variant opacity-70 block">
                              <Bilingual {...copy.evaluationDetail.optionAnswerKey} /> (Klik radio
                              untuk menandai kunci jawaban benar):
                            </span>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {q.options.map((opt, optIdx) => (
                                <label
                                  key={optIdx}
                                  className={`p-2.5 rounded-xl cursor-pointer transition-all flex items-center gap-2.5 text-xs font-body border ${
                                    opt.isCorrect
                                      ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-900 font-bold'
                                      : 'bg-surface-container-low hover:bg-surface-container border-on-surface/5 text-on-surface-variant'
                                  }`}
                                >
                                  <input
                                    type="radio"
                                    name={`correct-opt-${qIdx}`}
                                    checked={opt.isCorrect}
                                    onChange={() => updateParsedQuestionOption(qIdx, optIdx, true)}
                                    className="w-3.5 h-3.5 text-emerald-600 accent-emerald-600 bg-surface-container border-none focus:ring-0 cursor-pointer"
                                  />
                                  <span className="font-mono font-bold shrink-0">
                                    {String.fromCharCode(65 + optIdx)}.
                                  </span>
                                  <span className="flex-1 truncate">{opt.text}</span>
                                  {opt.isCorrect && (
                                    <CheckCircle2 size={14} className="text-emerald-600 shrink-0" />
                                  )}
                                </label>
                              ))}
                            </div>
                          </div>

                          {/* Explanation */}
                          {q.explanation && (
                            <div className="pl-7 pt-1 text-[11px] font-body text-on-surface-variant">
                              <span className="font-bold text-primary">Pembahasan: </span>
                              <span className="italic">{q.explanation}</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Error Recovery Banner */}
        {importError && (
          <div className="px-6 py-3.5 bg-error/10 border-t border-error/20 flex gap-3 items-center shrink-0">
            <AlertCircle size={16} className="text-error shrink-0" />
            <p className="text-xs font-body text-error flex-1 leading-snug m-0">{importError}</p>
            <button
              type="button"
              onClick={importMode === 'EXISTING' ? runImportFromExisting : runBulkAddFromText}
              className="text-[10px] font-label font-bold text-error uppercase tracking-widest hover:underline"
            >
              <Bilingual {...copy.evaluationDetail.btnRetry} />
            </button>
          </div>
        )}

        {/* Sticky Action Footer */}
        <div className="p-4 sm:p-6 border-t border-on-surface/5 bg-surface-container-low/40 flex items-center justify-between gap-4 shrink-0">
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            className="h-12 px-6 rounded-xl text-xs font-label font-bold uppercase tracking-widest"
          >
            <Bilingual {...copy.common.cancel} />
          </Button>

          {importMode === 'EXISTING' ? (
            <Button
              disabled={
                !selectedSourceId ||
                selectedSourceQuestionIds.length === 0 ||
                importQuestionsMutation.isPending
              }
              onClick={runImportFromExisting}
              isLoading={importQuestionsMutation.isPending}
              className="h-12 px-8 rounded-xl text-xs font-label font-bold uppercase tracking-widest shadow-level2"
            >
              {selectedSourceQuestionIds.length > 0
                ? t(copy.evaluationDetail.btnImportSelected).replace(
                    '{count}',
                    String(selectedSourceQuestionIds.length)
                  )
                : t(copy.evaluationDetail.btnImportNow)}
            </Button>
          ) : (
            <Button
              disabled={
                parsedQuestions.length === 0 ||
                selectedParsedIds.length === 0 ||
                bulkAddMutation.isPending
              }
              onClick={runBulkAddFromText}
              isLoading={bulkAddMutation.isPending}
              className="h-12 px-8 rounded-xl text-xs font-label font-bold uppercase tracking-widest shadow-level2"
            >
              {selectedParsedIds.length > 0
                ? t(copy.evaluationDetail.btnImportSelected).replace(
                    '{count}',
                    String(selectedParsedIds.length)
                  )
                : t(copy.evaluationDetail.btnImportNow)}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
